<template>
  <div class="szw-col szw-absolute szw-bg-background szw-gap-1 szw-p-2 szw-h-full szw-w-full szw-left-0 szw-top-0" :drf-id="widgetId">
    <caption-header :title="loc('sm_game_overview', props.languageIdentifier)" :subtitle="props.subtitle" :has-back="true" @back="closeDetails" />

    <div class="szw-flex szw-flex-col szw-h-full szw-w-full szw-max-w-[768px] szw-mx-auto szw-gap-1">
      <!-- EVENT BANNER -->
      <event-overview-banner
        :home="eventDetailsStore.getHomeTeam"
        :away="eventDetailsStore.getAwayTeam"
        :match-status="eventDetailsStore.getMatchStatus"
        :is-small-widget-container-size="isSmallWidgetContainerSize"
        :upcoming-event-start-time="eventDetailsStore.getUpcomingEventStartTime"
      />
      <template v-if="status !== 'error'">
        <template v-if="!eventDetailsStore.getIsUpcoming">
          <game-stats
            v-if="detailsType === 'stats'"
            :home-team="eventDetailsStore.homeTeam"
            :away-team="eventDetailsStore.awayTeam"
            :periods="eventDetailsStore.periods"
            :chart-stats="chartStats"
            :table-stats="tableStats"
          />
          <!-- GAME EVENTS -->
          <scrolling-container v-else-if="detailsType === 'events'" :containerRef="timelineRowsContainerRef" :widget-container-height="widgetContainerHeight">
            <div class="szw-col szw-w-full szw-absolute" ref="timelineRowsContainerRef" :style="{ transition: 'transform .2s linear' }" e2e-id="szw-key-moments">
              <event-row
                v-for="timelineEvent in eventDetailsStore.timeline.tickerData"
                :key="timelineEvent.id || timelineEvent.created + timelineEvent.minute + timelineEvent.personName"
                :team="getTeamMemo(timelineEvent.teamId)"
                :event="timelineEvent"
              />
            </div>
          </scrolling-container>
        </template>

        <template v-else>
          <scoreboard-widget
            :mode="'component'"
            :languageIdentifier="props.languageIdentifier"
            :sport-type="props.sportType"
            :event-id="props.event.id"
            :competitorId="eventDetailsStore.getHomeTeam.id"
            :competitor2Id="eventDetailsStore.getAwayTeam.id"
            :theme="props.theme"
            :size="props.size"
            :is-widget-injected="true"
            :region="region"
            :theme-config="props.themeConfig"
          />
        </template>
      </template>
      <template v-else>
        <div class="szw-flex szw-justify-center szw-h-full szw-items-center szw-text-center">
          {{ loc('sm_search_noresults', props.languageIdentifier) }}
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, provide, ref } from 'vue'
import CaptionHeader from '@/__core__/components/headers/caption-header/CaptionHeader.vue'
import EventOverviewBanner from '@/__core__/components/banners/EventOverviewBanner.vue'
import ScrollingContainer from '@/__core__/containers/scrolling-container/ScrollingContainer.vue'
import useFetch from '@/__core__/composables/useFetch'
import EventRow from './EventRow.vue'
import ScoreboardWidget from '@/widgets/scoreboard/ScoreboardWidget.vue'
import { useEventDetailsStore } from './EventDetails.store'
import { useDataRefresh } from '@/__core__/data-refresh/useDataRefresh'
import GameStats from '../game-stats/GameStats.vue'
import { adjustBasketballChartStats, adjustBasketballTableStats } from '@/widgets/statistics/Statistics.helpers'

const widgetId = 'event-details'

/** @type {{ detailsType: 'stats' | 'events' }} */
const props = defineProps({
  subtitle: {
    type: String,
    default: ''
  },
  event: {
    type: Object,
    default: () => ({
      id: '',
      teams: []
    })
  },
  isSmallWidgetContainerSize: {
    type: Boolean,
    default: false
  },
  sportType: {
    type: String,
    default: ''
  },
  region: {
    type: String,
    default: ''
  },
  languageIdentifier: {
    type: String,
    default: 'eng'
  },
  widgetContainerHeight: {
    type: Number,
    default: 0
  },
  theme: {
    type: String,
    default: 'light'
  },
  size: {
    type: Number,
    default: 640
  },
  detailsType: {
    type: String,
    default: 'events'
  },
  themeConfig: {
    type: Object,
    default: () => null
  }
})

const eventDetailsStore = useEventDetailsStore()
/* refresh interval is set to match fixtures refresh interval which is 5mins, because it will create bad UX if there are events that didn't happen on fixtures */
const { startRefresh } = useDataRefresh(5 * 60 * 1000, widgetId)

const timelineRowsContainerRef = ref(null)

const emit = defineEmits(['close-details'])

const { status } = useFetch(async () => {
  const payload = {
    event: props.event,
    eventId: props.event.id,
    sportType: props.sportType,
    region: props.region,
    languageIdentifier: props.languageIdentifier
  }

  await eventDetailsStore.fetchEventDetailsData(payload)
  let refreshFn = async () => Promise.reject(new Error('No refetch function provided'))
  const shouldCallApi = () => eventDetailsStore.getShouldCallApi()
  const shouldStopRefresh = () => eventDetailsStore.getShouldStopRefresh

  if (props.detailsType === 'stats') {
    refreshFn = () => eventDetailsStore.scheduleGameStatsRefresh(payload)
  } else if (props.detailsType === 'events') {
    refreshFn = async () => eventDetailsStore.scheduleEventDetailsRefresh(payload)
  }

  startRefresh(refreshFn, shouldCallApi, shouldStopRefresh)
})

provide('status', status)
provide('langId', props.languageIdentifier)

// REFACTOR this is coupled with the basketball sport type
const chartStats = computed(() => (status.value !== 'loading' ? adjustBasketballChartStats(eventDetailsStore.homeStats, eventDetailsStore.awayStats) : [1, 2, 3]))
const tableStats = computed(() => (status.value !== 'loading' ? adjustBasketballTableStats(eventDetailsStore.homeStats, eventDetailsStore.awayStats) : [1, 2, 3, 4, 5, 6]))

const getTeamMemo = computed(
  () => teamId =>
    ({
      [eventDetailsStore.getHomeTeam?.id]: eventDetailsStore.getHomeTeam,
      [eventDetailsStore.getAwayTeam?.id]: eventDetailsStore.getAwayTeam
    }[teamId] || {})
).value

const closeDetails = () => {
  emit('close-details')
}
</script>
